import React from "react";

function About() {
    return (
        <div className="pt-24 pb-16 text-[#78350f] min-h-screen">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Hero Header */}
                <div className="mb-16 text-center">
                    <div className="inline-block mb-6">
                        <div className="w-24 h-1 bg-gradient-to-r from-white via-[#fffbeb] to-white mx-auto rounded-full shadow-lg shadow-white/50"></div>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#78350f] to-[#92400e] bg-clip-text text-transparent drop-shadow-lg">
                        About TipTide
                    </h1>
                    <p className="text-[#92400e] text-xl md:text-2xl font-light">Established 2023 • Mobile Game Studio</p>
                    <div className="mt-8 inline-flex items-center gap-2 px-6 py-2 bg-white/20 border border-white/40 rounded-full">
                        <span className="w-2 h-2 bg-[#78350f] rounded-full animate-pulse"></span>
                        <span className="text-[#78350f] text-sm font-medium">Crafting Digital Adventures</span>
                    </div>
                </div>

                {/* Main Story Section */}
                <div className="relative mb-16">
                    {/* Decorative background elements */}
                    <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-10 -right-10 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>

                    <div className="relative bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-white/40 backdrop-blur-sm">
                        <div className="space-y-6 text-lg leading-relaxed">
                            <p className="text-[#78350f] text-xl font-light">
                                At <span className="font-bold text-[#92400e]">TipTide Games</span>, we ride the waves of creativity to craft experiences that move players — emotionally, intellectually, and playfully. Our passion lies in designing games that not only entertain but also connect people through meaningful stories, innovative mechanics, and unforgettable moments of discovery.
                            </p>

                            <p className="text-[#78350f]">
                                Founded by a team of <span className="font-semibold text-[#92400e]">dreamers, designers, and developers</span> who believe games are the most powerful art form of our time, TipTide Games blends imagination with technology to deliver titles that stand out for their depth, polish, and heart.
                            </p>

                            <p className="text-[#78350f]">
                                Whether it's a mobile adventure that brightens your day or an immersive world that challenges your perspective, every TipTide game is built with <span className="font-semibold text-[#92400e]">care, curiosity, and a deep respect</span> for players everywhere.
                            </p>

                            <div className="pt-6 border-t border-white/30">
                                <p className="text-[#92400e] text-2xl font-bold italic text-center">
                                    "We're not just making games — we're shaping tides of fun, creativity, and impact that ripple far beyond the screen."
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Core Values Grid */}
                <div className="mb-16">
                    <h2 className="text-4xl font-bold mb-12 text-center">
                        <span className="bg-gradient-to-r from-[#78350f] to-[#92400e] bg-clip-text text-transparent">
                            Our Core Values
                        </span>
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Value 1 */}
                        <div className="group bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 shadow-xl border-2 border-white/40 hover:border-white/60 transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-white/30 backdrop-blur-sm">
                            <div className="text-5xl mb-4 text-center group-hover:scale-110 transition-transform duration-300">🎮</div>
                            <h3 className="text-xl font-bold text-[#78350f] mb-3 text-center">Innovation</h3>
                            <p className="text-[#92400e] text-sm leading-relaxed text-center">
                                Pushing boundaries with fresh ideas and cutting-edge technology
                            </p>
                        </div>

                        {/* Value 2 */}
                        <div className="group bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 shadow-xl border-2 border-white/40 hover:border-white/60 transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-white/30 backdrop-blur-sm">
                            <div className="text-5xl mb-4 text-center group-hover:scale-110 transition-transform duration-300">✨</div>
                            <h3 className="text-xl font-bold text-[#78350f] mb-3 text-center">Quality</h3>
                            <p className="text-[#92400e] text-sm leading-relaxed text-center">
                                Every detail crafted with excellence and attention to perfection
                            </p>
                        </div>

                        {/* Value 3 */}
                        <div className="group bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 shadow-xl border-2 border-white/40 hover:border-white/60 transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-white/30 backdrop-blur-sm">
                            <div className="text-5xl mb-4 text-center group-hover:scale-110 transition-transform duration-300">🌊</div>
                            <h3 className="text-xl font-bold text-[#78350f] mb-3 text-center">Impact</h3>
                            <p className="text-[#92400e] text-sm leading-relaxed text-center">
                                Creating meaningful experiences that resonate with players
                            </p>
                        </div>

                        {/* Value 4 */}
                        <div className="group bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-2xl p-6 shadow-xl border-2 border-white/40 hover:border-white/60 transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl hover:shadow-white/30 backdrop-blur-sm">
                            <div className="text-5xl mb-4 text-center group-hover:scale-110 transition-transform duration-300">🤝</div>
                            <h3 className="text-xl font-bold text-[#78350f] mb-3 text-center">Community</h3>
                            <p className="text-[#92400e] text-sm leading-relaxed text-center">
                                Building connections and fostering a vibrant gaming community
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stats Section */}
                <div className="mb-16 relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/10 rounded-3xl blur-2xl"></div>
                    <div className="relative bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-white/40 backdrop-blur-sm">
                        <h2 className="text-3xl font-bold mb-10 text-center text-[#78350f]">Our Journey by Numbers</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center group">
                                <div className="text-5xl md:text-6xl font-extrabold text-[#78350f] mb-2 group-hover:scale-110 transition-transform duration-300">2+</div>
                                <div className="text-[#92400e] text-sm font-medium">Years of Excellence</div>
                            </div>
                            <div className="text-center group">
                                <div className="text-5xl md:text-6xl font-extrabold text-[#78350f] mb-2 group-hover:scale-110 transition-transform duration-300">∞</div>
                                <div className="text-[#92400e] text-sm font-medium">Creative Possibilities</div>
                            </div>
                            <div className="text-center group">
                                <div className="text-5xl md:text-6xl font-extrabold text-[#78350f] mb-2 group-hover:scale-110 transition-transform duration-300">100%</div>
                                <div className="text-[#92400e] text-sm font-medium">Passion Driven</div>
                            </div>
                            <div className="text-center group">
                                <div className="text-5xl md:text-6xl font-extrabold text-[#78350f] mb-2 group-hover:scale-110 transition-transform duration-300">🎯</div>
                                <div className="text-[#92400e] text-sm font-medium">Player Focused</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mission Section */}
                <div className="mb-16 relative">
                    <div className="bg-gradient-to-br from-white/30 via-[#fffbeb]/40 to-white/30 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-white/60 backdrop-blur-sm">
                        <div className="text-center">
                            <h2 className="text-4xl font-bold mb-6 text-[#78350f]">Our Mission</h2>
                            <p className="text-[#92400e] text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto font-light">
                                To create <span className="font-bold text-[#78350f]">exceptional mobile gaming experiences</span> that inspire, entertain, and bring people together through the power of play.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Vision Section */}
                <div className="mb-16 relative">
                    <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
                    <div className="relative bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-white/40 backdrop-blur-sm">
                        <div className="text-center">
                            <h2 className="text-4xl font-bold mb-6 text-[#78350f]">Our Vision</h2>
                            <p className="text-[#92400e] text-lg md:text-xl leading-relaxed max-w-4xl mx-auto">
                                To become a <span className="font-bold text-[#78350f]">leading force in mobile gaming</span>, recognized globally for our innovative gameplay, stunning visuals, and unwavering commitment to player satisfaction. We envision a future where every game we create becomes a beloved part of players' lives.
                            </p>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="text-center relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/10 rounded-3xl blur-2xl"></div>
                    <div className="relative bg-gradient-to-br from-[#fbbf24]/90 via-[#f59e0b]/90 to-[#fbbf24]/90 rounded-3xl p-10 md:p-16 shadow-2xl border-2 border-white/40 backdrop-blur-sm">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#78350f]">
                            Join Us on This Journey
                        </h2>
                        <p className="text-[#92400e] text-lg mb-8 max-w-2xl mx-auto">
                            Whether you're a player, developer, or dreamer — we'd love to connect with you.
                        </p>
                        <a
                            href="/contact"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-white hover:bg-[#fffbeb] rounded-full transition-all text-[#d97706] font-bold text-lg shadow-2xl shadow-white/40 hover:shadow-white/60 hover:scale-105 transform duration-300"
                        >
                            <span>Get in Touch</span>
                            <span className="text-xl">→</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default About;
